/**
 * MissionLocationMap — the one mission map (mission-map unification, PR6) for the create/edit dialog
 * (mode="edit") and the mission detail (mode="readonly").
 *
 * A mission has a take-off point (latitude/longitude — required, the address is derived from it) and
 * exactly one flight area (`flight_area`): a Polygon, a LineString flown inside a corridor of
 * `corridor_width_m`, or a circle (a Point centre with `circle_radius_m`). The map is a controlled
 * component: the parent owns the point, the area, the width and the radius and gets every change
 * through the callbacks; the server's settings and validation result come in as props (see
 * lib/flightArea.js) and drive the panel under the map.
 *
 * Edit mode: address search (Nominatim from the browser), click into the map / drag the drone marker
 * to place the take-off point, Leaflet Draw toolbar (polygon / line / circle, edit, delete), a corridor
 * width popup after a line is drawn and from the "Corridor width" action of a route's vertex edit (the
 * same value as the panel's width field; cancelling the vertex edit restores it), lat/lon inputs.
 * A circle gets a radius popup after it is drawn and from the "Radius" action of its edit; during that
 * edit the popup and the panel field write a radius draft that the map applies to the circle and Save
 * confirms (see FlightAreaLayer), and the take-off marker stays visible but cannot be grabbed, so the
 * circle's handles under it can. "Take-off at the circle centre" moves the take-off point to the centre
 * and keeps it there when the circle is moved; a manual move of the point unticks it (not stored — on
 * opening it is ticked when the point lies at the centre).
 * Read-only: the same layers without any editing; airport markers and points of interest on request.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Navigation, AlertTriangle, Loader2, Crosshair } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
  classifyFlightArea, collectLatLngs, FLIGHT_AREA_KIND, isMapClickSuppressed, circleRadiusLimitsFromSettings,
  getCircleCenter, getCircleRadiusIssue, isPointAtCircleCenter, CIRCLE_DRAFT_IDLE,
} from '../lib/flightArea';
import { loadEnabledThemes, saveEnabledThemes, toggleTheme } from '../lib/uasZones';
import {
  UasZonesLayer,
  UasZonesControl,
  AirspaceControlPanel,
  AirportMarkerLayer,
  MapLegend,
  CriticalObjectsLayer,
  MapLayerControl,
  BASE_MAPS,
  FlightAreaLayer,
  FlightAreaPanel,
  takeoffIconEditable,
  takeoffIconReadonly,
} from './map';

// Leaflet's default marker images are referenced by CSS-relative URLs that webpack breaks; the
// critical-objects layer still uses default markers.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const DEFAULT_CENTER = [49.8, 15.5]; // Czech Republic
const DEFAULT_ZOOM = 7;
const POINT_ZOOM = 14;
const OVERVIEW_ZOOM_BELOW = 10;

const isValidPoint = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng)
  && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

// Nominatim geocoding (free, no API key)
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      { headers: { Accept: 'application/json' } }
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), displayName: data[0].display_name };
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Nominatim address parts naming the municipality, in order of preference.
const MUNICIPALITY_KEYS = ['village', 'town', 'city', 'municipality', 'hamlet', 'suburb'];

// Reverse geocoding — the municipality of a point (used to prefill the mission's site name), null
// without one. The first part of the full address is often a house or road number, not a site name.
const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      { headers: { Accept: 'application/json' } }
    );
    const data = await response.json();
    const address = data?.address || {};
    const key = MUNICIPALITY_KEYS.find((k) => typeof address[k] === 'string' && address[k].trim());
    return key ? address[key].trim() : null;
  } catch (error) {
    return null;
  }
};

/** Keeps Leaflet's size in sync with its container (dialog animation, responsive two-column layout). */
const ResizeHandler = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const timer = setTimeout(() => map.invalidateSize(), 250);
    if (typeof ResizeObserver === 'undefined') return () => clearTimeout(timer);
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);
  return null;
};

/** Fits the view to the area + take-off point once per `fitKey` (mount, mission switch, import). */
const FitToContent = ({ fitKey, geometry, circleRadiusM, latitude, longitude }) => {
  const map = useMap();
  const latest = useRef({ geometry, circleRadiusM, latitude, longitude });
  latest.current = { geometry, circleRadiusM, latitude, longitude };
  useEffect(() => {
    const { geometry: g, circleRadiusM: r, latitude: lat, longitude: lng } = latest.current;
    const points = collectLatLngs(g, r);
    if (isValidPoint(lat, lng)) points.push([lat, lng]);
    // After the container has its final size (dialog open animation).
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (points.length > 1) {
        map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
      } else if (points.length === 1) {
        map.setView(points[0], POINT_ZOOM);
      }
    }, 260);
    return () => clearTimeout(timer);
  }, [map, fitKey]);
  return null;
};

/** Brings the take-off point into a usable view when it is set from outside the current view or from
 * an overview zoom (search, typed coordinates, first click on the country map). */
const KeepPointInView = ({ latitude, longitude }) => {
  const map = useMap();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!isValidPoint(latitude, longitude)) return;
    const point = L.latLng(latitude, longitude);
    if (!map.getBounds().contains(point) || map.getZoom() < OVERVIEW_ZOOM_BELOW) {
      map.setView(point, Math.max(map.getZoom(), POINT_ZOOM));
    }
  }, [map, latitude, longitude]);
  return null;
};

const MapClickHandler = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
};

export const MissionLocationMap = ({
  mode = 'readonly',
  locked = false,
  latitude,
  longitude,
  onLocationChange,
  onAddressChange,
  flightArea = null,
  corridorWidthM = null,
  circleRadiusM = null,
  onFlightAreaChange,
  onCorridorWidthChange,
  onCircleRadiusChange,
  settings = null,
  validation = null,
  fitKey = 'initial',
  height = '300px',
  className = '',
  showSearch = true,
  showTakeoffInputs = true,
  showPanel = true,
  showAirspaceZones = false,
  nearbyAirports = [],
  onImportClick,
  importAvailable = false,
  highlightWidth = false,
  onMapSessionChange,
  mapyCzApiKey = null,
  stadiaApiKey = null,
  // Mission date ("YYYY-MM-DD" or ISO): the UAS zones shown are those valid on it; null = today.
  zonesDate = null,
}) => {
  const { t, language, translate } = useLanguage();
  const fa = t.flightArea || {};
  const editable = mode === 'edit' && !locked;

  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [placingTakeoff, setPlacingTakeoff] = useState(false);
  // A Leaflet Draw draw / edit / delete session is open: its geometry is not in the form yet, so the
  // parent blocks the mission save and the panel blocks import / delete until it ends. The ref is the
  // synchronous copy for the map click handler (the state lags behind leaflet-draw's events).
  const [drawActive, setDrawActive] = useState(false);
  const sessionRef = useRef({ active: false, endedAt: 0 });
  const handleDrawActiveChange = useCallback((active) => {
    sessionRef.current = { active, endedAt: active ? 0 : Date.now() };
    setDrawActive(active);
  }, []);
  const [widthPopupAt, setWidthPopupAt] = useState(null);
  const [radiusPopupAt, setRadiusPopupAt] = useState(null);
  // The radius draft of a circle's vertex edit (CIRCLE_DRAFT_IDLE outside it, see FlightAreaLayer).
  const [circleDraft, setCircleDraft] = useState(CIRCLE_DRAFT_IDLE);
  // A circle is being drawn or edited: the take-off marker is passive so the handles under it can be grabbed.
  const [circleSession, setCircleSession] = useState(false);
  const [takeoffAtCenter, setTakeoffAtCenter] = useState(false);
  // Each change of the take-off point bumps it — a reverse-geocoded address (or a search result) that
  // arrives after a newer change is dropped.
  const addressSeqRef = useRef(0);
  const [currentBaseMap, setCurrentBaseMap] = useState('osm');
  const [showAirspaceLayer, setShowAirspaceLayer] = useState(showAirspaceZones);
  const [showCriticalObjects, setShowCriticalObjects] = useState(false);
  const [criticalObjects, setCriticalObjects] = useState([]);
  const [zoneThemes, setZoneThemes] = useState(loadEnabledThemes);
  const [zoneStatus, setZoneStatus] = useState(null);
  const handleToggleZoneTheme = useCallback((theme) => {
    setZoneThemes((prev) => {
      const next = toggleTheme(prev, theme);
      saveEnabledThemes(next);
      return next;
    });
  }, []);

  const hasLocation = isValidPoint(latitude, longitude);
  const areaKind = classifyFlightArea(flightArea).kind;
  const isCircle = areaKind === FLIGHT_AREA_KIND.CIRCLE;
  const circleRadiusLimits = useMemo(() => circleRadiusLimitsFromSettings(settings), [settings]);
  const takeoffPassive = editable && circleSession;

  // Points of interest around the take-off point (read-only detail).
  useEffect(() => {
    if (!showAirspaceZones || !hasLocation) return undefined;
    let cancelled = false;
    api.get(`/airspace/critical-objects?latitude=${latitude}&longitude=${longitude}&radius_km=50`)
      .then((response) => { if (!cancelled) setCriticalObjects(response.data?.objects || []); })
      .catch((error) => {
        console.warn('Failed to fetch critical objects:', error);
        if (!cancelled) setCriticalObjects([]);
      });
    return () => { cancelled = true; };
  }, [latitude, longitude, showAirspaceZones, hasLocation]);

  const sessionCallbackRef = useRef(onMapSessionChange);
  sessionCallbackRef.current = onMapSessionChange;
  useEffect(() => {
    sessionCallbackRef.current?.(drawActive);
  }, [drawActive]);
  // A map unmounted mid-session (dialog closed) must not leave the parent blocked.
  useEffect(() => () => sessionCallbackRef.current?.(false), []);
  // Nor may an address (or a search result) answered after it is gone fill in the next form.
  useEffect(() => () => { ++addressSeqRef.current; }, []);

  // The width popup belongs to a line only, the radius popup and the centre checkbox to a circle only.
  useEffect(() => {
    if (areaKind !== FLIGHT_AREA_KIND.LINE) setWidthPopupAt(null);
    if (areaKind !== FLIGHT_AREA_KIND.CIRCLE) {
      setRadiusPopupAt(null);
      setTakeoffAtCenter(false);
    }
  }, [areaKind]);

  // On opening (and for another mission) the checkbox says whether the take-off point is at the centre.
  const fitInputs = useRef({ latitude, longitude, flightArea });
  fitInputs.current = { latitude, longitude, flightArea };
  useEffect(() => {
    const { latitude: lat, longitude: lng, flightArea: area } = fitInputs.current;
    setTakeoffAtCenter(isPointAtCircleCenter(lat, lng, area, 1));
  }, [fitKey]);

  // No radius draft outlives the map sessions (the layer drops it itself; this is the safety net).
  useEffect(() => {
    if (!drawActive) setCircleDraft(CIRCLE_DRAFT_IDLE);
  }, [drawActive]);

  const setTakeoff = useCallback(async (lat, lng) => {
    const seq = ++addressSeqRef.current;
    onLocationChange?.(lat, lng);
    if (onAddressChange) {
      const address = await reverseGeocode(lat, lng);
      if (address && seq === addressSeqRef.current) onAddressChange(address);
    }
  }, [onLocationChange, onAddressChange]);

  // A take-off point placed by hand (click, drag, search, coordinates) is no longer "at the centre".
  const moveTakeoffByHand = useCallback((lat, lng) => {
    setTakeoffAtCenter(false);
    return setTakeoff(lat, lng);
  }, [setTakeoff]);

  const handleMapClick = useCallback((latlng) => {
    if (!editable || isMapClickSuppressed(sessionRef.current)) return;
    if (placingTakeoff || !hasLocation) {
      setPlacingTakeoff(false);
      moveTakeoffByHand(latlng.lat, latlng.lng);
    }
  }, [editable, placingTakeoff, hasLocation, moveTakeoffByHand]);

  // A circle finishes on mouseup, after which the zones are clickable again: the click that follows
  // must not open the zones popup over the radius popup.
  const isZoneClickSuppressed = useCallback(() => isMapClickSuppressed(sessionRef.current), []);

  const handleCoordinateInput = (lat, lng) => {
    setTakeoffAtCenter(false);
    ++addressSeqRef.current;
    onLocationChange?.(lat, lng);
  };

  const handleTakeoffAtCenterChange = (checked) => {
    setTakeoffAtCenter(checked);
    const center = checked ? getCircleCenter(flightArea) : null;
    if (center) setTakeoff(center.lat, center.lng);
  };

  // A drawn / edited area from the toolbar: a new circle starts with the checkbox unticked, a moved
  // circle takes a take-off point kept at its centre along.
  const handleFlightAreaChange = useCallback((geometry, meta) => {
    onFlightAreaChange?.(geometry, meta);
    if (meta?.source === 'created') {
      setTakeoffAtCenter(false);
      return;
    }
    if (meta?.source !== 'edited' || !takeoffAtCenter) return;
    const center = getCircleCenter(geometry);
    if (center && !isPointAtCircleCenter(latitude, longitude, geometry, 1)) setTakeoff(center.lat, center.lng);
  }, [onFlightAreaChange, takeoffAtCenter, latitude, longitude, setTakeoff]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const seq = ++addressSeqRef.current;
    setSearching(true);
    setSearchError(null);
    try {
      const result = await geocodeAddress(searchQuery);
      // The take-off point was moved meanwhile — that later change wins.
      if (seq !== addressSeqRef.current) return;
      if (result) {
        setTakeoffAtCenter(false);
        onLocationChange?.(result.lat, result.lng);
        if (onAddressChange) onAddressChange(result.displayName);
      } else {
        setSearchError(fa.searchNotFound);
      }
    } catch (err) {
      if (seq === addressSeqRef.current) setSearchError(fa.searchFailed);
    } finally {
      setSearching(false);
    }
  };

  const handleLineCreated = useCallback((latlng) => {
    if ((corridorWidthM === null || corridorWidthM === undefined || corridorWidthM === '') && settings) {
      onCorridorWidthChange?.(settings.corridor_width_default_m);
    }
    setWidthPopupAt(latlng);
  }, [corridorWidthM, settings, onCorridorWidthChange]);

  // The radius popup and the panel field write the draft during a circle's vertex edit, the form outside it.
  const handleCircleRadiusDraftInput = useCallback((value) => {
    setCircleDraft({ active: true, value: value === '' || value === null ? null : value, origin: 'input' });
  }, []);
  const radiusInSession = circleDraft.active;
  const radiusInputValue = radiusInSession ? circleDraft.value : circleRadiusM;
  const radiusIssue = isCircle ? getCircleRadiusIssue(radiusInputValue, circleRadiusLimits) : null;
  // During the edit an invalid draft keeps the popup open (Save is blocked until it is fixed).
  const radiusPopupLocked = radiusInSession && radiusIssue !== null;
  const closeRadiusPopup = () => {
    if (!radiusPopupLocked) setRadiusPopupAt(null);
  };
  const handleRadiusPopupInput = (raw) => {
    const value = raw === '' ? null : raw;
    if (radiusInSession) handleCircleRadiusDraftInput(value);
    else onCircleRadiusChange?.(value);
  };

  const handleDeleteArea = () => {
    // The panel button is disabled during a session; the toolbar's own layer would win over it.
    if (sessionRef.current.active) return;
    if (window.confirm(fa.deleteConfirm)) onFlightAreaChange?.(null);
  };

  const baseMap = BASE_MAPS[currentBaseMap] || BASE_MAPS.osm;
  let tileUrl = baseMap.url;
  if (baseMap.requiresKey && baseMap.urlTemplate) {
    if (currentBaseMap === 'mapycz' && mapyCzApiKey) tileUrl = baseMap.urlTemplate.replace('{apikey}', mapyCzApiKey);
    else if (currentBaseMap === 'stadia_outdoors' && stadiaApiKey) tileUrl = baseMap.urlTemplate.replace('{apikey}', stadiaApiKey);
    else tileUrl = BASE_MAPS.osm.url;
  }

  const parseCoordinate = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {mode === 'edit' && showSearch && (
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={fa.searchPlaceholder}
              value={searchQuery}
              disabled={!editable}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              className="pl-9"
              data-testid="location-search-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleSearch}
            disabled={!editable || searching || !searchQuery.trim()}
            className="gap-2"
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
            {fa.search}
          </Button>
          <Button
            type="button"
            variant={placingTakeoff ? 'default' : 'outline'}
            onClick={() => setPlacingTakeoff((v) => !v)}
            disabled={!editable}
            className="gap-2"
            data-testid="place-takeoff-btn"
          >
            <Crosshair className="h-4 w-4" />
            {placingTakeoff ? fa.setTakeoffActive : fa.setTakeoff}
          </Button>
        </div>
      )}

      {searchError && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {searchError}
        </p>
      )}

      {/* `isolate`: Leaflet's panes/controls (z-index 200–1000) and the overlays below stay inside the map
          and do not paint over the sticky app header (z-30) when the page scrolls. */}
      <div
        className={`rounded-lg overflow-hidden border border-slate-200 relative isolate ${placingTakeoff ? 'cursor-crosshair' : ''}`}
        style={{ height }}
        data-testid="mission-map"
      >
        <MapContainer
          center={hasLocation ? [latitude, longitude] : DEFAULT_CENTER}
          zoom={hasLocation ? POINT_ZOOM : DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            key={currentBaseMap}
            attribution={baseMap.attribution}
            url={tileUrl}
            maxZoom={baseMap.maxZoom || 19}
          />
          <ResizeHandler />
          <FitToContent
            fitKey={fitKey}
            geometry={flightArea}
            circleRadiusM={circleRadiusM}
            latitude={latitude}
            longitude={longitude}
          />
          <KeepPointInView latitude={latitude} longitude={longitude} />

          {/* Zones ignore clicks while a click places the take-off point or draws the flight area. */}
          <UasZonesLayer
            enabledThemes={zoneThemes}
            date={zonesDate}
            language={language}
            labels={t.uasZones}
            interactive={!editable || (hasLocation && !placingTakeoff && !drawActive)}
            isClickSuppressed={isZoneClickSuppressed}
            onStatusChange={setZoneStatus}
          />

          <CriticalObjectsLayer objects={criticalObjects} showLayer={showCriticalObjects} language={language} />
          {hasLocation && <AirportMarkerLayer airports={nearbyAirports} showLayer={showAirspaceLayer} />}

          <FlightAreaLayer
            geometry={flightArea}
            corridorWidthM={corridorWidthM}
            circleRadiusM={circleRadiusM}
            circleRadiusDraft={circleDraft}
            circleRadiusLimits={circleRadiusLimits}
            editable={editable}
            onGeometryChange={handleFlightAreaChange}
            onDrawActiveChange={handleDrawActiveChange}
            onLineCreated={handleLineCreated}
            onCorridorWidthChange={onCorridorWidthChange}
            onCorridorWidthRequest={setWidthPopupAt}
            corridorWidthActionText={fa.corridorWidthAction}
            corridorWidthActionTitle={fa.corridorWidthActionTitle}
            onCircleCreated={setRadiusPopupAt}
            onCircleRadiusChange={onCircleRadiusChange}
            onCircleRadiusDraft={setCircleDraft}
            onCircleRadiusRequest={setRadiusPopupAt}
            onCircleSessionChange={setCircleSession}
            circleRadiusActionText={fa.circleRadiusAction}
            circleRadiusActionTitle={fa.circleRadiusActionTitle}
            confirmReplaceText={fa.replaceConfirm}
            language={language}
          />

          {editable && <MapClickHandler onClick={handleMapClick} />}

          {/* While a circle is drawn or edited the marker stays visible but passive and under the
              circle's handles: `interactive` only applies when a marker is created, hence the key. */}
          {hasLocation && (
            <Marker
              key={takeoffPassive ? 'takeoff-passive' : 'takeoff'}
              position={[latitude, longitude]}
              icon={editable ? takeoffIconEditable : takeoffIconReadonly}
              interactive={!takeoffPassive}
              draggable={editable && !takeoffPassive}
              zIndexOffset={takeoffPassive ? -1000 : 1000}
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  moveTakeoffByHand(lat, lng);
                },
              }}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{fa.takeoffPoint}</p>
                  <p className="text-xs mt-1 font-mono">{latitude.toFixed(6)}, {longitude.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {editable && widthPopupAt && areaKind === FLIGHT_AREA_KIND.LINE && (
            <Popup
              position={widthPopupAt}
              eventHandlers={{ remove: () => setWidthPopupAt(null) }}
            >
              <div className="space-y-2 min-w-[190px]" data-testid="corridor-width-popup">
                <label htmlFor="corridor-width-popup-input" className="block text-xs font-semibold text-slate-700">
                  {fa.corridorWidth}
                </label>
                <input
                  id="corridor-width-popup-input"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={settings?.corridor_width_min_m}
                  max={settings?.corridor_width_max_m}
                  value={corridorWidthM ?? ''}
                  autoFocus
                  onChange={(e) => onCorridorWidthChange?.(e.target.value === '' ? null : e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Never submit the surrounding mission form from inside the map.
                      e.preventDefault();
                      setWidthPopupAt(null);
                    }
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
                {settings && (
                  <p className="text-[11px] text-slate-500">
                    {settings.corridor_width_min_m}–{settings.corridor_width_max_m} m
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setWidthPopupAt(null)}
                  className="w-full rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                >
                  {fa.corridorApply}
                </button>
              </div>
            </Popup>
          )}

          {/* A circle is finished on mouseup, so the popup is already open when the click that follows
              reaches the map; `closeOnClick={false}` keeps it open (Apply, Enter or its cross close it). */}
          {editable && radiusPopupAt && isCircle && (
            <Popup
              position={radiusPopupAt}
              closeOnClick={false}
              eventHandlers={{ remove: () => setRadiusPopupAt(null) }}
            >
              <div className="space-y-2 min-w-[190px]" data-testid="circle-radius-popup">
                <label htmlFor="circle-radius-popup-input" className="block text-xs font-semibold text-slate-700">
                  {fa.circleRadius}
                </label>
                <input
                  id="circle-radius-popup-input"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={circleRadiusLimits?.min}
                  max={circleRadiusLimits?.max}
                  value={radiusInputValue ?? ''}
                  autoFocus
                  onChange={(e) => handleRadiusPopupInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Never submit the surrounding mission form from inside the map.
                      e.preventDefault();
                      closeRadiusPopup();
                    }
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  data-testid="circle-radius-popup-input"
                />
                {circleRadiusLimits && (
                  <p className="text-[11px] text-slate-500">{circleRadiusLimits.min}–{circleRadiusLimits.max} m</p>
                )}
                {radiusIssue === 'required' && <p className="text-[11px] text-red-700">{fa.circleRadiusRequired}</p>}
                {radiusIssue === 'range' && circleRadiusLimits && (
                  <p className="text-[11px] text-red-700">{translate('flightArea.circleRadiusRange', circleRadiusLimits)}</p>
                )}
                {radiusPopupLocked && <p className="text-[11px] text-slate-600">{fa.circleRadiusFixBeforeSave}</p>}
                <button
                  type="button"
                  onClick={closeRadiusPopup}
                  disabled={radiusPopupLocked}
                  className="w-full rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600"
                  data-testid="circle-radius-popup-apply"
                >
                  {fa.corridorApply}
                </button>
              </div>
            </Popup>
          )}
        </MapContainer>

        {/* Map controls — top right (Leaflet Draw toolbar sits top left under the zoom buttons) */}
        <div className="absolute top-2 right-2 z-[1000] flex flex-col items-end gap-1">
          <MapLayerControl
            language={language}
            mapyCzApiKey={mapyCzApiKey}
            stadiaApiKey={stadiaApiKey}
            currentBaseMap={currentBaseMap}
            onBaseMapChange={(mapId) => setCurrentBaseMap(mapId)}
          />
          <UasZonesControl
            enabledThemes={zoneThemes}
            onToggleTheme={handleToggleZoneTheme}
            status={zoneStatus}
            language={language}
            labels={t.uasZones}
          />
          {showAirspaceZones && hasLocation && (
            <AirspaceControlPanel
              showAirspaceLayer={showAirspaceLayer}
              setShowAirspaceLayer={setShowAirspaceLayer}
              showCriticalObjects={showCriticalObjects}
              setShowCriticalObjects={setShowCriticalObjects}
              showAirspaceZones={showAirspaceZones}
              language={language}
            />
          )}
        </div>

        <MapLegend
          hasTakeoff={hasLocation}
          areaKind={areaKind}
          showCriticalObjects={showCriticalObjects}
          criticalObjects={criticalObjects}
          language={language}
          labels={fa}
        />

        {editable && !drawActive && (!hasLocation || placingTakeoff) && (
          <div className="absolute inset-x-0 bottom-3 z-[1000] flex justify-center pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border">
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                {fa.takeoffHelper}
              </p>
            </div>
          </div>
        )}
      </div>

      {mode === 'edit' && showTakeoffInputs && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label htmlFor="mission-latitude" className="text-xs text-slate-500">{fa.takeoffPoint} — {fa.latitude}</label>
            <Input
              id="mission-latitude"
              type="number"
              step="any"
              placeholder={fa.latitude}
              value={latitude ?? ''}
              disabled={!editable}
              onChange={(e) => handleCoordinateInput(parseCoordinate(e.target.value), longitude ?? null)}
              data-testid="mission-latitude"
              className="bg-white border-slate-300 text-slate-900"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="mission-longitude" className="text-xs text-slate-500">{fa.takeoffPoint} — {fa.longitude}</label>
            <Input
              id="mission-longitude"
              type="number"
              step="any"
              placeholder={fa.longitude}
              value={longitude ?? ''}
              disabled={!editable}
              onChange={(e) => handleCoordinateInput(latitude ?? null, parseCoordinate(e.target.value))}
              data-testid="mission-longitude"
              className="bg-white border-slate-300 text-slate-900"
            />
          </div>
        </div>
      )}

      {editable && isCircle && (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={takeoffAtCenter}
            // Not during a map session: the circle being drawn or edited is not in the form yet.
            disabled={drawActive}
            onChange={(e) => handleTakeoffAtCenterChange(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
            data-testid="takeoff-at-circle-center"
          />
          {fa.takeoffAtCircleCenter}
        </label>
      )}

      {showPanel && (
        <FlightAreaPanel
          mode={mode}
          locked={locked}
          geometry={flightArea}
          corridorWidthM={corridorWidthM}
          onCorridorWidthChange={onCorridorWidthChange}
          circleRadiusM={circleRadiusM}
          onCircleRadiusChange={onCircleRadiusChange}
          circleRadiusDraft={circleDraft}
          onCircleRadiusDraftInput={handleCircleRadiusDraftInput}
          circleRadiusLimits={circleRadiusLimits}
          latitude={hasLocation ? latitude : null}
          longitude={hasLocation ? longitude : null}
          validation={validation}
          settings={settings}
          onDeleteArea={handleDeleteArea}
          onImportClick={onImportClick}
          importAvailable={importAvailable}
          highlightWidth={highlightWidth}
          sessionActive={drawActive}
        />
      )}
    </div>
  );
};

export default MissionLocationMap;
